import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const SiteContentCtx = createContext({ content: {}, refresh: () => {}, loaded: false });

export const SiteContentProvider = ({ children }) => {
    const [content, setContent] = useState({});
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        try {
            // append a timestamp so any browser/CDN cache is bypassed on refresh
            const { data } = await api.get(`/site-content?t=${Date.now()}`);
            setContent(data || {});
        } catch {
            setContent({});
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <SiteContentCtx.Provider value={{ content, refresh, loaded }}>{children}</SiteContentCtx.Provider>
    );
};

export const useSiteContent = () => useContext(SiteContentCtx);

// helper: get a content string for current lang, fallback to t()
export const useSiteText = (lang, t) => {
    const { content } = useSiteContent();
    return (key) => {
        const composite = `${key}_${lang}`;
        const val = content[composite];
        if (val != null && val !== "") return val;
        // fallback to i18n dictionary
        return t ? t(key) : composite;
    };
};

export const useSiteFlag = (key, defaultVal = true) => {
    const { content, loaded } = useSiteContent();
    if (!loaded) return defaultVal; // avoid flashing hidden while loading
    if (content[key] == null) return defaultVal;
    return content[key] === "1" || content[key] === true;
};
